import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, TrendingUp, MessageCircle, Clock, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface MessageInsightsProps {
  companyId: string;
}

interface Insight {
  id: string;
  period_start: string;
  period_end: string;
  top_questions: string[];
  category_breakdown: Record<string, number>;
  response_times: {
    average_hours: number;
    samples: number;
    key_themes: string[];
    recommendations: string[];
  };
  generated_at: string;
}

export function MessageInsights({ companyId }: MessageInsightsProps) {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [insight, setInsight] = useState<Insight | null>(null);

  async function loadLatestInsight() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('investor_message_insights')
        .select('*')
        .eq('company_id', companyId)
        .order('generated_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setInsight({
          id: data.id,
          period_start: data.period_start,
          period_end: data.period_end,
          top_questions: Array.isArray(data.top_questions)
            ? (data.top_questions as string[]).filter((q): q is string => typeof q === 'string')
            : [],
          category_breakdown:
            typeof data.category_breakdown === 'object' && data.category_breakdown !== null
              ? (data.category_breakdown as Record<string, number>)
              : {},
          response_times:
            typeof data.response_times === 'object' && data.response_times !== null
              ? {
                  average_hours: (data.response_times as any).average_hours || 0,
                  samples: (data.response_times as any).samples || 0,
                  key_themes: Array.isArray((data.response_times as any).key_themes)
                    ? ((data.response_times as any).key_themes as string[]).filter(
                        (t): t is string => typeof t === 'string'
                      )
                    : [],
                  recommendations: Array.isArray((data.response_times as any).recommendations)
                    ? ((data.response_times as any).recommendations as string[]).filter(
                        (r): r is string => typeof r === 'string'
                      )
                    : [],
                }
              : { average_hours: 0, samples: 0, key_themes: [], recommendations: [] },
          generated_at: data.generated_at,
        });
      } else {
        setInsight(null);
      }
    } catch (error: any) {
      console.error('Error loading insights:', error);
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  }

  async function generateInsights() {
    setGenerating(true);
    try {
      const { error } = await supabase.functions.invoke('generate-message-insights', {
        body: { companyId, periodDays: 90 },
      });

      if (error) throw error;

      toast.success('Insights generated successfully');
      await loadLatestInsight();
    } catch (error: any) {
      toast.error(error.message || 'Failed to generate insights');
      console.error('Error generating insights:', error);
    } finally {
      setGenerating(false);
    }
  }

  if (loading) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="p-12 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Ella Insights
          </CardTitle>
          <Button
            size="sm"
            onClick={insight ? loadLatestInsight : generateInsights}
            disabled={generating}
          >
            {generating ? (
              <>
                <Loader2 className="animate-spin h-4 w-4 mr-2" />
                Generating...
              </>
            ) : (
              <>
                <TrendingUp className="h-4 w-4 mr-2" />
                {insight ? 'Refresh' : 'Generate Insights'}
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {!insight ? (
          <div className="p-8 text-center border border-dashed border-border rounded-lg">
            <Lightbulb className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-sm text-muted-foreground">No insights generated yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Click "Generate Insights" to analyze your investor messages
            </p>
          </div>
        ) : (
          <>
            {/* Period Info */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>
                Period: {new Date(insight.period_start).toLocaleDateString()} -{' '}
                {new Date(insight.period_end).toLocaleDateString()}
              </span>
            </div>

            {/* Top Questions */}
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <MessageCircle className="h-4 w-4" />
                Most Asked Questions
              </h3>
              <ul className="space-y-2">
                {insight.top_questions.map((q, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <Badge variant="secondary" className="mt-0.5">
                      {idx + 1}
                    </Badge>
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Category Breakdown */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Message Categories</h3>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(insight.category_breakdown).map(([cat, count]) => (
                  <div
                    key={cat}
                    className="flex items-center justify-between p-3 rounded-lg bg-accent/30"
                  >
                    <span className="text-sm font-medium">{cat}</span>
                    <Badge variant="outline">{count}</Badge>
                  </div>
                ))}
              </div>
            </div>

            {/* Response Time */}
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Avg Response Time</span>
                <span className="text-2xl font-bold text-primary">
                  {insight.response_times.average_hours}h
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Based on {insight.response_times.samples} conversations
              </p>
            </div>

            {/* Key Themes */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Key Themes</h3>
              <div className="flex flex-wrap gap-2">
                {insight.response_times.key_themes.map((theme, idx) => (
                  <Badge key={idx} variant="secondary">
                    {theme}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Recommendations */}
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Lightbulb className="h-4 w-4" />
                Recommendations
              </h3>
              <ul className="space-y-2">
                {insight.response_times.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <span className="text-primary mt-0.5">•</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-xs text-muted-foreground text-center pt-4 border-t">
              Generated by Ella AI on {new Date(insight.generated_at).toLocaleString()}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
